import { memo, useCallback, useEffect, useState } from "react";

import { uploadFleetDocumentAPI } from "services/api/fleet/vehicles/documents/api";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";

import Modal from "ui/components/Modal/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { TextArea } from "ui/components/TextField/TextArea";
import { FileDropZone } from "ui/components/FileUpload/FileDropZone";
import Loader from "ui/components/Loader/Loader";
import FileUploadList from "ui/components/FileUpload/FileUploadList/FileUploadList";

import { FileUploadModalProps } from "./types";

const FileUploadModal: React.FC<FileUploadModalProps> = (props) => {
  const {
    show,
    onHide,
    onSave,
    title,
    size,
    maxFiles,
    showNameField,
    showDescriptionField,
    nameLabel = "Naziv",
    namePlaceholder = "Unesite naziv",
    descriptionLabel = "Opis",
    descriptionPlaceholder = "Unesite opis",
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploads, setUploads] = useState<FleetDocumentUpload[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();

  useEffect(() => {
    if (!show) return;
    setUploads([]);
    setName("");
    setDescription("");
    setNameError(undefined);
    setIsLoading(false);
  }, [show]);

  const handleFileChange = useCallback(
    async (newFiles: File[]) => {
      if (maxFiles) {
        const remaining = maxFiles - uploads.length;
        if (remaining <= 0) return;
        newFiles = newFiles.slice(0, remaining);
      }

      setIsLoading(true);
      try {
        const uploaded = await uploadFleetDocumentAPI(newFiles);
        setUploads((prev) => [...prev, ...uploaded]);
      } catch {
        // error handled by API interceptors
      } finally {
        setIsLoading(false);
      }
    },
    [maxFiles, uploads.length]
  );

  const handleDelete = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const handleSave = useCallback(() => {
    if (showNameField && !name.trim()) {
      setNameError("Molimo unesite naziv");
      return;
    }

    const meta = (showNameField || showDescriptionField)
      ? { name: name.trim(), description: description.trim() }
      : undefined;

    onSave(uploads, meta);
    onHide();
  }, [uploads, name, description, showNameField, showDescriptionField, onSave, onHide]);

  const isMaxReached = maxFiles ? uploads.length >= maxFiles : false;
  const showDropZone = !isMaxReached && (!showNameField || name.trim().length > 0);

  return (
    <Modal
      show={show}
      position="centered"
      size={size ?? "small"}
      onHide={onHide}

    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={title ?? "Upload datoteka"}
        onHide={onHide}
      />
      <ModalBody>
        <div className="px-4 pt-5 pb-8 ">
          {showNameField && (
            <div className="mb-3">
            <TextField
              type="text"
              name="name"
              label={nameLabel}
              placeholder={namePlaceholder}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setName(e.target.value);
                setNameError(undefined);
              }}
              error={nameError}
              required
            />
            </div>
          )}

          {showDescriptionField && (
            <TextArea
              name="description"
              label={descriptionLabel}
              placeholder={descriptionPlaceholder}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.target.value)
              }
            />
          )}

          {showDropZone && (
            <div className="relative">
              {isLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                  <Loader isLoading={true}>{null}</Loader>
                </div>
              )}
              <FileDropZone
                onChange={handleFileChange}
                onDelete={() => { }}
                files={[]}
              />
            </div>
          )}

          <FileUploadList files={uploads} onDelete={handleDelete} />
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
          <Button variant="outlined" onClick={onHide}>
            Odustani
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={uploads.length === 0 || isLoading}
          >
            Spremi
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(FileUploadModal);
